import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Pencil, Check, X, Trash2, StopCircle, ChevronRight, Loader2 } from 'lucide-react'
import {
  listAnalyses,
  deleteAnalysis,
  cancelAnalysis,
  renameAnalysis,
  type AnalysisListItem,
  type AnalysisListType,
  type AnalysisStatus,
} from '../api/analysis'

interface AnalysisJobsPanelProps {
  type: 'ai_batch' | 'topic'
  onLoad?: (id: string, item: AnalysisListItem) => void
  title?: string
}

const STATUS_STYLES: Record<AnalysisStatus, string> = {
  pending: 'bg-slate-100 text-slate-700',
  running: 'bg-blue-100 text-blue-700',
  complete: 'bg-emerald-100 text-emerald-700',
  error: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-200 text-gray-600',
}

function relativeTime(iso: string): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return ''
  const diff = (Date.now() - t) / 1000
  if (diff < 60) return `${Math.max(0, Math.floor(diff))}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return `${Math.floor(diff / 86400)}d ago`
}

export default function AnalysisJobsPanel({ type, onLoad, title = 'Jobs' }: AnalysisJobsPanelProps) {
  const queryClient = useQueryClient()
  const queryKey = ['analyses-list', type]

  const { data, isFetching } = useQuery({
    queryKey,
    queryFn: () => listAnalyses({ type: type as AnalysisListType, limit: 100 }),
    staleTime: 0,
    refetchInterval: (q: any) => {
      const rows = (q?.state?.data as AnalysisListItem[] | undefined) || []
      return rows.some(r => r.status === 'running' || r.status === 'pending') ? 3000 : false
    },
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey })

  const deleteMutation = useMutation({
    mutationFn: deleteAnalysis,
    onSuccess: invalidate,
    onError: (err: any) => alert(err?.response?.data?.detail || 'Delete failed'),
  })

  const cancelMutation = useMutation({
    mutationFn: cancelAnalysis,
    onSuccess: invalidate,
    onError: (err: any) => alert(err?.response?.data?.detail || 'Cancel failed'),
  })

  const renameMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameAnalysis(id, name),
    onSuccess: invalidate,
    onError: (err: any) => alert(err?.response?.data?.detail || 'Rename failed'),
  })

  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameDraft, setRenameDraft] = useState('')

  const rows = data || []

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        <button
          onClick={invalidate}
          disabled={isFetching}
          className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="text-xs text-slate-400">No analyses yet. Launch one and it'll show up here.</div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto">
          {rows.map(row => {
            const isRenaming = renamingId === row.analysis_id
            const isRunning = row.status === 'running' || row.status === 'pending'
            const canLoad = row.status === 'complete' && !!onLoad
            return (
              <div
                key={row.analysis_id}
                className="flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-slate-50 border border-transparent hover:border-slate-200"
              >
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${STATUS_STYLES[row.status] || STATUS_STYLES.pending}`}>
                  {row.status}
                </span>

                {isRenaming ? (
                  <>
                    <input
                      autoFocus
                      value={renameDraft}
                      onChange={(e) => setRenameDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          renameMutation.mutate({ id: row.analysis_id, name: renameDraft })
                          setRenamingId(null)
                        } else if (e.key === 'Escape') {
                          setRenamingId(null)
                        }
                      }}
                      className="flex-1 min-w-0 px-1.5 py-0.5 border border-slate-300 rounded text-xs"
                    />
                    <button
                      onClick={() => {
                        renameMutation.mutate({ id: row.analysis_id, name: renameDraft })
                        setRenamingId(null)
                      }}
                      className="p-0.5 text-emerald-600 hover:text-emerald-700"
                      title="Save"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setRenamingId(null)}
                      className="p-0.5 text-slate-500 hover:text-slate-700"
                      title="Cancel"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <>
                    <span className="flex-1 min-w-0 truncate" title={row.name}>{row.name}</span>
                    {row.dataset_name && (
                      <span
                        className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-violet-100 text-violet-700 truncate max-w-[8rem]"
                        title={`Dataset: ${row.dataset_name}`}
                      >
                        {row.dataset_name}
                      </span>
                    )}
                    {row.search_query && (
                      <span className="text-slate-400 truncate max-w-[10rem]" title={row.search_query}>
                        · {row.search_query}
                      </span>
                    )}
                    <span className="text-slate-400 text-[10px] shrink-0">{relativeTime(row.created_at)}</span>
                    <button
                      onClick={() => {
                        setRenameDraft(row.name)
                        setRenamingId(row.analysis_id)
                      }}
                      className="p-0.5 text-slate-300 hover:text-slate-700"
                      title="Rename"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    {canLoad && (
                      <button
                        onClick={() => onLoad?.(row.analysis_id, row)}
                        className="px-2 py-0.5 text-[11px] bg-blue-100 text-blue-700 rounded hover:bg-blue-200 inline-flex items-center gap-0.5"
                        title="Load results"
                      >
                        Load <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                    {isRunning && (
                      <button
                        onClick={() => {
                          if (window.confirm(`Cancel "${row.name}"?`)) cancelMutation.mutate(row.analysis_id)
                        }}
                        disabled={cancelMutation.isPending}
                        className="p-0.5 text-amber-600 hover:text-amber-700 disabled:opacity-50"
                        title="Cancel"
                      >
                        {cancelMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <StopCircle className="w-3.5 h-3.5" />}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(`Delete "${row.name}"?`)) deleteMutation.mutate(row.analysis_id)
                      }}
                      disabled={deleteMutation.isPending}
                      className="p-0.5 text-slate-300 hover:text-red-600 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
